import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ApiPageResponse } from '../types/api.types';
import {
  AdminCreateOperatorRequest,
  AdminUpdateOperatorRequest,
  ApplyOperatorRequest,
  ApproveOperatorRequest,
  CashReconciliationReport,
  CreditReferenceRequest,
  EffectiveRatesResponse,
  Operator,
  OperatorDashboardResponse,
  OperatorDebtReconciliationView,
  OperatorView,
} from '../types/operator.types';
import {
  ModerationAction,
  PrestataireCard,
  PrestataireLink,
  PrestataireResolveResponse,
  PrestataireType,
  RecruitOutcomeResponse,
  RecruitPrestataireRequest,
  ReportPrestataireRequest,
  SuspendPrestataireRequest,
  WarnPrestataireRequest,
} from '../types/prestataire.types';
import {
  ChangeRequestDirection,
  ChangeRequestStatus,
  ChangeRequestView,
} from '../types/change-request.types';

@Injectable({ providedIn: 'root' })
export class OperatorsService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.gatewayUrl}/operator/api`;

  /**
   * Candidature self-service de l'utilisateur courant — `POST /operators/apply`.
   * Aucun rôle requis (JWT seul). Crée un Operator `PENDING` ; le rôle OPERATOR
   * n'est assigné qu'à l'approbation admin. Renvoie 409 si une candidature
   * existe déjà pour ce user.
   */
  apply(body: ApplyOperatorRequest): Observable<OperatorView> {
    return this.http.post<OperatorView>(`${this.base}/operators/apply`, body);
  }

  /**
   * Tableau de bord de l'opérateur courant — `GET /operators/me/dashboard`.
   * Soldes wallet + KPIs prestataires/commissions. Renvoie 404 si le user
   * authentifié n'a pas encore d'opérateur lié (cf. flow candidature `/apply`).
   */
  myDashboard(): Observable<OperatorDashboardResponse> {
    return this.http.get<OperatorDashboardResponse>(`${this.base}/operators/me/dashboard`);
  }

  /**
   * Dernier rapport de réconciliation cash hebdomadaire (§8.4) —
   * `GET /operators/me/cash-reconciliation/latest`. **204 → aucun rapport encore
   * généré** : HttpClient renvoie alors `null` (body vide).
   */
  cashReconciliationLatest(): Observable<CashReconciliationReport | null> {
    return this.http.get<CashReconciliationReport | null>(
      `${this.base}/operators/me/cash-reconciliation/latest`,
    );
  }

  /**
   * Historique paginé des rapports (§8.4) —
   * `GET /operators/me/cash-reconciliation?page&size`. Pagination 1-indexée +
   * enveloppe Nawam, trié `weekStart` desc.
   */
  cashReconciliationHistory(page = 1, size = 20): Observable<ApiPageResponse<CashReconciliationReport>> {
    const params = new HttpParams().set('page', String(page)).set('size', String(size));
    return this.http.get<ApiPageResponse<CashReconciliationReport>>(
      `${this.base}/operators/me/cash-reconciliation`,
      { params },
    );
  }

  /**
   * Rapprochement **temps réel** « dû vs réglé » + aging de mes prestataires (#50) —
   * `GET /operators/me/debt-reconciliation`. **204 → indisponible** : HttpClient
   * renvoie alors `null`. Un seul objet `OperatorDebtReconciliationView`.
   */
  myDebtReconciliation(): Observable<OperatorDebtReconciliationView | null> {
    return this.http.get<OperatorDebtReconciliationView | null>(
      `${this.base}/operators/me/debt-reconciliation`,
    );
  }

  /**
   * L'opérateur déclare qu'un de SES prestataires lui a réglé sa dette **en
   * espèces** (hors plateforme) — `POST /operators/me/prestataires/{type}/{id}/settle-debt-cash`
   * body `{ amount }`. Le back vérifie que le prestataire est bien rattaché à
   * l'opérateur courant (le créancier), éponge la dette et renvoie le **montant
   * réellement épongé** (BigDecimal → number ; ≤ `amount` si la dette est plus
   * faible que le montant déclaré).
   */
  settleDebtCash(
    type: PrestataireType,
    id: number,
    amount: number,
  ): Observable<number> {
    return this.http.post<number>(
      `${this.base}/operators/me/prestataires/${type}/${id}/settle-debt-cash`,
      { amount },
    );
  }

  // ── Prestataires & modération (operator self-service) ───────────────────

  /** Prestataires actifs liés à l'opérateur courant — `GET /operators/me/prestataires`.
   *  Fiches enrichies à plat (`PrestataireCard` : identité + note + véhicules +
   *  dette). Enveloppe Nawam, pagination 1-indexée. */
  myPrestataires(page = 1, size = 20): Observable<ApiPageResponse<PrestataireCard>> {
    const params = new HttpParams().set('page', String(page)).set('size', String(size));
    return this.http.get<ApiPageResponse<PrestataireCard>>(
      `${this.base}/operators/me/prestataires`,
      { params },
    );
  }

  /**
   * Résout une personne par identifiant (username | email | téléphone) pour le
   * recrutement — `GET /operators/me/prestataires/resolve?identifier=`. Toujours
   * 200 : `userFound`/`alreadyOwner` pilotent le mode (A/B/C). Cf. §13.B.
   */
  resolvePrestataire(identifier: string): Observable<PrestataireResolveResponse> {
    const params = new HttpParams().set('identifier', identifier.trim());
    return this.http.get<PrestataireResolveResponse>(
      `${this.base}/operators/me/prestataires/resolve`,
      { params },
    );
  }

  /**
   * Recrute un prestataire. 403 si EXHAUSTED. Renvoie un `RecruitOutcomeResponse` :
   * `LINKED` (créé à l'instant → rattaché) ou `REQUEST_PENDING` (existant → demande
   * de changement à accepter par le prestataire). Cf. §13.D / #36.
   */
  recruitPrestataire(body: RecruitPrestataireRequest): Observable<RecruitOutcomeResponse> {
    return this.http.post<RecruitOutcomeResponse>(
      `${this.base}/operators/me/prestataires/recruit`,
      body,
    );
  }

  // ── Demandes de changement d'opérateur (#36) ────────────────────────────

  /** Mes demandes de changement — `GET /operators/me/change-requests`.
   *  `incoming` = initiées par des prestataires (à décider) ; `outgoing` = les miennes. */
  listChangeRequests(
    direction: ChangeRequestDirection,
    status?: ChangeRequestStatus,
    page = 1,
    size = 20,
  ): Observable<ApiPageResponse<ChangeRequestView>> {
    let params = new HttpParams()
      .set('direction', direction)
      .set('page', String(page))
      .set('size', String(size));
    if (status) params = params.set('status', status);
    return this.http.get<ApiPageResponse<ChangeRequestView>>(
      `${this.base}/operators/me/change-requests`,
      { params },
    );
  }

  /** Accepte une demande entrante (initiée par un prestataire). @RequiresActiveOperator + quota. */
  acceptChangeRequest(id: number): Observable<ChangeRequestView> {
    return this.http.post<ChangeRequestView>(
      `${this.base}/operators/me/change-requests/${id}/accept`,
      {},
    );
  }

  /** Refuse une demande entrante. */
  rejectChangeRequest(id: number): Observable<ChangeRequestView> {
    return this.http.post<ChangeRequestView>(
      `${this.base}/operators/me/change-requests/${id}/reject`,
      {},
    );
  }

  /** Annule une de mes demandes sortantes (PENDING). */
  cancelChangeRequest(id: number): Observable<ChangeRequestView> {
    return this.http.post<ChangeRequestView>(
      `${this.base}/operators/me/change-requests/${id}/cancel`,
      {},
    );
  }

  /** Libère un prestataire (retour au fallback). Autorisé même en EXHAUSTED. */
  releasePrestataire(type: PrestataireType, id: number): Observable<PrestataireLink> {
    return this.http.post<PrestataireLink>(
      `${this.base}/operators/me/prestataires/${type}/${id}/release`,
      {},
    );
  }

  /** Avertit un prestataire. 403 si EXHAUSTED. */
  warnPrestataire(
    type: PrestataireType,
    id: number,
    body: WarnPrestataireRequest,
  ): Observable<ModerationAction> {
    return this.http.post<ModerationAction>(
      `${this.base}/operators/me/prestataires/${type}/${id}/warn`,
      body,
    );
  }

  /** Suspend un prestataire (1–72 h). 403 si EXHAUSTED. */
  suspendPrestataire(
    type: PrestataireType,
    id: number,
    body: SuspendPrestataireRequest,
  ): Observable<ModerationAction> {
    return this.http.post<ModerationAction>(
      `${this.base}/operators/me/prestataires/${type}/${id}/suspend`,
      body,
    );
  }

  /** Escalade un prestataire à la plateforme. Autorisé même en EXHAUSTED. */
  reportPrestataire(
    type: PrestataireType,
    id: number,
    body: ReportPrestataireRequest,
  ): Observable<ModerationAction> {
    return this.http.post<ModerationAction>(
      `${this.base}/operators/me/prestataires/${type}/${id}/report`,
      body,
    );
  }

  /** Paginated admin list — `GET /operators?page&size&zoneCode`. */
  list(opts: { page?: number; size?: number; zoneCode?: string } = {}): Observable<ApiPageResponse<OperatorView>> {
    let params = new HttpParams()
      .set('page', String(opts.page ?? 1))
      .set('size', String(opts.size ?? 20));
    if (opts.zoneCode?.trim()) params = params.set('zoneCode', opts.zoneCode.trim());
    return this.http.get<ApiPageResponse<OperatorView>>(
      `${this.base}/operators`,
      { params },
    );
  }

  /** Single operator (full entity) — `GET /operators/{id}`. */
  get(id: number): Observable<Operator> {
    return this.http.get<Operator>(`${this.base}/operators/${id}`);
  }

  /** Approve a PENDING operator. Body is optional. */
  approve(id: number, body?: ApproveOperatorRequest): Observable<OperatorView> {
    return this.http.post<OperatorView>(
      `${this.base}/operators/${id}/approve`,
      body ?? {},
    );
  }

  /** Update or clear the credit reference override (admin). */
  setCreditReference(id: number, body: CreditReferenceRequest): Observable<OperatorView> {
    return this.http.put<OperatorView>(
      `${this.base}/admin/operators/${id}/credit-reference`,
      body,
    );
  }

  /** Admin creates an operator for a given userId — `POST /admin/operators`. */
  create(body: AdminCreateOperatorRequest): Observable<OperatorView> {
    return this.http.post<OperatorView>(`${this.base}/admin/operators`, body);
  }

  /** Partial edit of business fields — `PATCH /admin/operators/{id}`. */
  update(id: number, body: AdminUpdateOperatorRequest): Observable<OperatorView> {
    return this.http.patch<OperatorView>(`${this.base}/admin/operators/${id}`, body);
  }

  /** Effective commission rates for this operator (override merged with global).
   *  `usesCustomRates` is true if the override is active, false if globals apply. */
  effectiveRates(id: number): Observable<EffectiveRatesResponse> {
    return this.http.get<EffectiveRatesResponse>(
      `${this.base}/operators/${id}/effective-rates`,
    );
  }
}
